/**
 * A Map-like data structure that serializes keys before storage.
 * Useful when keys are complex objects that need to be compared by value rather than reference.
 */
export class SerializedKeyMap<K, V> {
  private map: Map<string, {key: K; value: V}> = new Map();
  private keySerializer: (key: K) => string;

  constructor(keySerializer: (key: K) => string);
  constructor(keySerializer: (key: K) => string, iterable: Iterable<[K, V]>);
  constructor(other: SerializedKeyMap<K, V>);

  constructor(keySerializerOrOther: ((key: K) => string) | SerializedKeyMap<K, V>, iterable?: Iterable<[K, V]>) {
    if (typeof keySerializerOrOther === "function") {
      this.keySerializer = keySerializerOrOther;

      if (iterable) {
        for (const [key, value] of iterable) {
          this.set(key, value);
        }
      }
    } else {
      this.keySerializer = keySerializerOrOther.keySerializer;
      for (const [key, value] of keySerializerOrOther.entries()) {
        this.set(key, value);
      }
    }
  }

  set(key: K, value: V): this {
    const serializedKey = this.keySerializer(key);
    this.map.set(serializedKey, {key, value});
    return this;
  }

  get(key: K): V | undefined {
    const serializedKey = this.keySerializer(key);
    return this.map.get(serializedKey)?.value;
  }

  has(key: K): boolean {
    const serializedKey = this.keySerializer(key);
    return this.map.has(serializedKey);
  }

  delete(key: K): boolean {
    const serializedKey = this.keySerializer(key);
    return this.map.delete(serializedKey);
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  *keys(): MapIterator<K> {
    for (const entry of this.map.values()) {
      yield entry.key;
    }
  }

  *values(): MapIterator<V> {
    for (const entry of this.map.values()) {
      yield entry.value;
    }
  }

  *entries(): MapIterator<[K, V]> {
    for (const entry of this.map.values()) {
      yield [entry.key, entry.value];
    }
  }

  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.entries();
  }

  forEach(callback: (value: V, key: K, map: SerializedKeyMap<K, V>) => void): void {
    for (const entry of this.map.values()) {
      callback(entry.value, entry.key, this);
    }
  }
}
